// Le parc : un véhicule par carte, l'échéance la plus pressante en évidence.
//
// L'ordre suit l'urgence, pas l'alphabet : ce qui est dépassé remonte, les
// véhicules sortis du parc descendent sans disparaître.

import { filtrerRecherche } from '@imp/shared/recherche';
import { construireParc, nomVehicule, type UrgenceEcheance } from '@imp/shared/vehicules';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Gauge, Plus, Truck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatEuro } from '@/modules/catalogue/api';
import {
  libelleTypeVehicule,
  listInterventions,
  listVehicules,
  versInterventionSuivi,
  versSuivi,
} from '@/modules/vehicules/api';

const URGENCE_STYLE: Record<UrgenceEcheance, string> = {
  depassee: 'bg-money-out-soft text-money-out',
  proche: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  a_venir: 'bg-muted text-muted-foreground',
};

export function VehiculesListPage() {
  const [q, setQ] = useState('');
  const vehiculesQ = useQuery({ queryKey: ['vehicules'], queryFn: listVehicules });
  const interventionsQ = useQuery({
    queryKey: ['vehicule-interventions'],
    queryFn: listInterventions,
  });

  const parc = useMemo(
    () =>
      construireParc(
        (vehiculesQ.data ?? []).map(versSuivi),
        (interventionsQ.data ?? []).map(versInterventionSuivi),
        Date.now(),
      ),
    [vehiculesQ.data, interventionsQ.data],
  );

  // Recherche universelle : nom, immatriculation, type et échéances affichées.
  const filtre = useMemo(
    () =>
      filtrerRecherche(parc, q, (l) => [
        nomVehicule(l.vehicule),
        l.vehicule.immatriculation,
        libelleTypeVehicule(l.vehicule.type),
        l.urgente?.texte,
      ]),
    [parc, q],
  );

  const aTraiter = parc.filter(
    (l) => l.urgente?.urgence === 'depassee' || l.urgente?.urgence === 'proche',
  ).length;
  const totalAnnee = parc.reduce((t, l) => t + l.couts.totalHt, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Véhicules</h1>
          <p className="text-sm text-muted-foreground">
            Entretien, réparations, révisions, contrôle technique et assurance.
          </p>
        </div>
        <Button asChild>
          <Link to="/vehicules/nouveau">
            <Plus className="h-4 w-4" /> Ajouter un véhicule
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Véhicules</div>
          <div className="text-2xl font-bold">{parc.filter((l) => l.vehicule.actif).length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">À traiter</div>
          <div className={`text-2xl font-bold ${aTraiter > 0 ? 'text-money-out' : ''}`}>
            {aTraiter}
          </div>
        </Card>
        <Card className="col-span-2 p-4 sm:col-span-1">
          <div className="text-xs uppercase text-muted-foreground">Dépenses enregistrées</div>
          <div className="text-2xl font-bold tabular-nums">{formatEuro(totalAnnee)}</div>
        </Card>
      </div>

      {parc.length > 0 ? (
        <BarreRecherche
          valeur={q}
          onChange={setQ}
          quoi="un véhicule"
          champs="nom, immatriculation, type"
          resultats={filtre.length}
          total={parc.length}
        />
      ) : null}

      {vehiculesQ.isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : parc.length === 0 ? (
        <Card className="space-y-3 p-8 text-center">
          <Truck className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">
            Aucun véhicule enregistré. Ajoutez la première camionnette pour tenir son carnet
            d&apos;entretien.
          </p>
          <Button asChild>
            <Link to="/vehicules/nouveau">
              <Plus className="h-4 w-4" /> Ajouter un véhicule
            </Link>
          </Button>
        </Card>
      ) : filtre.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">Aucun résultat.</Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtre.map((l) => (
            <Card key={l.vehicule.id} className={l.vehicule.actif ? '' : 'opacity-60'}>
              <Link to={`/vehicules/${l.vehicule.id}`} className="block p-4 hover:bg-muted/30">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{nomVehicule(l.vehicule)}</div>
                    <div className="text-xs text-muted-foreground">
                      {libelleTypeVehicule(l.vehicule.type)}
                      {l.vehicule.actif ? '' : ' · sorti du parc'}
                    </div>
                  </div>
                  {l.urgente ? (
                    <span
                      className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${URGENCE_STYLE[l.urgente.urgence]}`}
                    >
                      {l.urgente.urgence === 'depassee' ? (
                        <AlertTriangle className="h-3 w-3" aria-hidden />
                      ) : null}
                      {l.urgente.texte}
                    </span>
                  ) : (
                    <span className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                      Aucune échéance notée
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Gauge className="h-3.5 w-3.5" aria-hidden />
                    {l.vehicule.kilometrage === null
                      ? 'km non relevé'
                      : `${l.vehicule.kilometrage.toLocaleString('fr-FR')} km`}
                    {l.relevePerime && l.vehicule.kilometrage !== null ? ' (à rafraîchir)' : ''}
                  </span>
                  <span>
                    {l.couts.nb} intervention{l.couts.nb > 1 ? 's' : ''}
                    {l.couts.totalHt > 0 ? ` · ${formatEuro(l.couts.totalHt)} HT` : ''}
                  </span>
                </div>

                {l.echeances.length > 1 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {l.echeances.slice(1).map((e) => (
                      <span
                        key={e.cle}
                        className={`rounded px-1.5 py-0.5 text-[11px] ${URGENCE_STYLE[e.urgence]}`}
                      >
                        {e.texte}
                      </span>
                    ))}
                  </div>
                ) : null}
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
